import { useEffect } from 'react';
import NetworkManager from '../../app/managers/networkManager';

/**
 * Warms collab profile pictures into the browser cache, in background chunks.
 *
 * The avatars themselves are lazy — a row that has not been scrolled to does not fetch its
 * picture — which keeps the first paint cheap but means scrolling fast can reach a face
 * before its image has loaded. This walks the collabs a chunk at a time after the list is
 * in place, so by the time the user scrolls the pictures are already local.
 *
 * It costs nothing on a second visit: these are the same versioned URLs the <img> tags use,
 * so a warmed picture is served from disk cache and a request is never repeated. That is
 * the whole reason the picture is a URL rather than base64 in the collab listing — bytes
 * inlined in JSON would have to be re-downloaded on every page load, chunked or not.
 *
 * @param {Array} collabs collab objects carrying `id` and `photoUpdatedAt`
 * @param {object} [options]
 * @param {number} [options.chunkSize] pictures started per chunk
 * @param {number} [options.delayMs] pause between chunks
 */

// Module-level, so navigating away from the planning and back does not re-warm what is
// already warm. Holds URLs, which are versioned — a new upload produces a new URL and is
// therefore warmed again, exactly as it should be.
const warmedUrls = new Set();

export function usePhotoPrefetch(collabs, { chunkSize = 25, delayMs = 150 } = {}) {

    // A stable key rather than the array itself: redux hands back a fresh array reference on
    // unrelated updates, and depending on that would restart the walk constantly. This
    // changes only when the actual set of pictures does.
    const identity = Array.isArray(collabs)
        ? collabs
            .filter((collab) => collab && collab.id && collab.photoUpdatedAt)
            .map((collab) => `${collab.id}:${new Date(collab.photoUpdatedAt).getTime()}`)
            .sort()
            .join(',')
        : '';

    useEffect(() => {
        if (!identity) return undefined;

        const networkManager = NetworkManager.getInstance();
        const pending = [];

        identity.split(',').forEach((entry) => {
            const [id, version] = entry.split(':');
            const url = networkManager.getCollabPhotoUrl(id, Number(version));
            if (url && !warmedUrls.has(url)) pending.push(url);
        });

        if (!pending.length) return undefined;

        let cancelled = false;
        let timer = null;

        const loadChunk = (startIndex) => {
            if (cancelled || startIndex >= pending.length) return;

            pending.slice(startIndex, startIndex + chunkSize).forEach((url) => {
                // Marked before the request, not after: a chunk that is still in flight must
                // not be queued a second time by a re-render.
                warmedUrls.add(url);
                const image = new Image();
                // Decoding off the main thread — this is background work and must not
                // compete with the planning's own rendering.
                image.decoding = 'async';
                image.src = url;
            });

            // Spaced out rather than fired all at once so the warm-up never queues ahead of
            // a real API call the user is waiting on.
            timer = setTimeout(() => loadChunk(startIndex + chunkSize), delayMs);
        };

        loadChunk(0);

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            // In-flight requests are deliberately NOT aborted: they are populating the cache,
            // which stays useful whether or not this view is still mounted. Only the
            // scheduling of further chunks stops.
        };
    }, [identity, chunkSize, delayMs]);
}

export default usePhotoPrefetch;
